import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { AdminTagEditor } from "../admin/AdminTagEditor";
import { AdminVerifyToggle } from "../admin/AdminVerifyToggle";
import { useMe } from "../auth/useMe";
import { CheckInButton } from "../checkIns/CheckInButton";
import { HeartButton } from "../favorites/HeartButton";
import { useOpenIssuesCount } from "../issues/hooks";
import { channelManager } from "../realtime/ChannelManager";
import { ReportForm } from "../reports/ReportForm";
import { ReportList } from "../reports/ReportList";
import { useReportsForStation } from "../reports/hooks";
import { isFastConnector, type ChargerInfo, type StationDetail as StationDetailType } from "./api";
import { AvailabilityPill, type AvailabilityTone } from "./AvailabilityPill";
import { CONNECTOR_LABEL, formatKw, sortPlugGroups, type ConnectorType } from "./connectors";
import { StationMapPreview } from "./StationMapPreview";
import { StationPhoto } from "./StationPhoto";
import { StatusFreshnessBadge } from "./StatusFreshnessBadge";

type Props = {
  station: StationDetailType;
};

export function StationDetail({ station }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const reports = useReportsForStation(station.id);

  // location.key is "default" only on a fresh load with no in-app history
  // (deep link / refresh) — fall back to the charger list so back never strands.
  const goBack = () =>
    location.key === "default" ? navigate("/chargers") : navigate(-1);
  const me = useMe();
  const issuesQuery = useOpenIssuesCount(station.id);
  const issuesCount = issuesQuery.data ?? 0;
  const isAdmin = me.data?.role === "admin";
  const showPhoto = station.photoStatus === "approved" && !!station.photoUrl;
  const showPlaceholder = station.photoStatus === "pending";
  const heroPresent = showPhoto || showPlaceholder;

  useEffect(() => channelManager.subscribeToStation(station.id), [station.id]);

  const maxPower = useMemo(
    () =>
      station.chargers.length > 0
        ? Math.max(...station.chargers.map((c) => c.power_kw))
        : 0,
    [station.chargers],
  );

  const directionsUrl = navigateUrl(station);

  return (
    <article className="space-y-5 pb-16 md:pb-0">
      {/* pb-16 clears the fixed nav anchor (bottom-[88px] h-12) above AppShell's pb-[88px] floor; at md+ there is no bottom nav */}
      <button
        type="button"
        onClick={goBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-mute hover:text-fg"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 18l-6-6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t("stations.back")}
      </button>
      {heroPresent && (
        <Hero
          showPhoto={showPhoto}
          photoUrl={station.photoUrl ?? null}
          stationName={station.name}
          stationId={station.id}
        />
      )}

      <header className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-bold text-fg">{station.name}</h1>
          {!heroPresent && <HeartButton stationId={station.id} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusFreshnessBadge
            status={station.status}
            lastActivityAt={station.lastActivityAt}
            size="md"
            testIdSuffix={station.id}
          />
          <AvailabilityPill chargers={station.chargers} testIdSuffix={station.id} />
        </div>
        {station.address && <p className="text-sm text-fg-mute">{station.address}</p>}
        {station.verified && (
          <span className="inline-block rounded border border-line px-2 py-0.5 text-xs text-fg-mute">
            {t("stations.verified")}
          </span>
        )}
        {issuesQuery.isError && (
          <p className="text-xs text-ember">{t("stations.issues_count_failed")}</p>
        )}
        {!issuesQuery.isError && issuesCount > 0 && (
          <p className="num inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/5 px-3 py-1 text-xs font-semibold text-amber">
            <span aria-hidden="true">⚠</span>
            {t(
              issuesCount === 1
                ? "stations.open_issues_badge"
                : "stations.open_issues_badge_plural",
              { count: issuesCount },
            )}
          </p>
        )}
      </header>

      <StationMapPreview
        lat={station.lat}
        lng={station.lng}
        stationName={station.name}
        navigateUrl={directionsUrl}
      />

      {/*
        Two reading tracks at lg+: reference (stats + plugs) left, interactive (reports + form) right.
        DOM order = left track then right, so no-CSS linearization stays stats → plugs → reports → form.
        Breakpoint is lg (1024px), not md (768px): at md the desktop shell's 240px sidebar +
        320px rail leave the main panel at ~208px, which md:grid-cols-2 would halve into ~88px
        columns and overlap content. At lg the main panel is ~464px (~216px per column) — fits.
      */}
      <div
        data-testid="station-detail-body"
        className="space-y-5 lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8 lg:space-y-0"
      >
        <div className="space-y-5">
          <StatsRow maxPower={maxPower} pricingInfo={station.pricingInfo} />
          <OperatorSection company={station.company} paymentNote={station.paymentNote} />
          <PlugsSection chargers={station.chargers} />
        </div>

        <div className="space-y-5">
          <section>
            <h2 className="label-caps mb-2 text-fg-mute">{t("stations.recent_reports_heading")}</h2>
            {reports.isLoading && <p className="text-sm text-fg-mute">{t("common.loading")}</p>}
            {reports.isError && (
              <p className="text-sm text-ember">{t("stations.reports_load_failed")}</p>
            )}
            {reports.data && <ReportList reports={reports.data} />}
          </section>

          <section>
            <h2 className="label-caps mb-2 text-fg-mute">{t("stations.report_heading")}</h2>
            <ReportForm stationId={station.id} />
          </section>
        </div>
      </div>

      <CheckInButton station={{ id: station.id, lat: station.lat, lng: station.lng }} />

      {isAdmin && (
        <div className="space-y-3">
          <AdminVerifyToggle stationId={station.id} verified={station.verified} />
          <AdminTagEditor stationId={station.id} initialTags={station.tags} />
        </div>
      )}

      {/*
        md offset = AppShell DesktopShell rail column (grid-cols-[240px_1fr_320px]) + gap,
        so the fixed CTA floats at the main-panel bottom-right, clear of the rail. If that
        rail width changes, update this.
        Intentionally NOT volt-stroke: that class sets position: relative for its ::after
        underline (sidebar tabs / bottom-tab indicators), which overrides Tailwind's
        `fixed` and leaves the CTA in normal page flow. The underline pseudo-element
        also doesn't visually belong on a rounded-full pill.
      */}
      <a
        href={directionsUrl}
        className="fixed bottom-[88px] left-1/2 z-30 flex h-12 -translate-x-1/2 items-center gap-2 rounded-full bg-volt px-6 text-sm font-semibold text-on-volt shadow-volt hover:bg-volt-bright md:bottom-6 md:left-auto md:right-[calc(320px_+_1.5rem)] md:translate-x-0"
      >
        <span aria-hidden="true">🧭</span>
        {t("stations.start_navigation")}
      </a>
    </article>
  );
}

function Hero({
  showPhoto,
  photoUrl,
  stationName,
  stationId,
}: {
  showPhoto: boolean;
  photoUrl: string | null;
  stationName: string;
  stationId: string;
}) {
  return (
    <div
      className="relative -mx-5 overflow-hidden bg-line-strong md:mx-0"
      style={{
        // Design-system carve-out: CSS variable lookup var(--color-cyan) cannot be expressed as a Tailwind class
        boxShadow: "inset 0 -40px 60px -40px var(--color-cyan)",
      }}
    >
      <div className="flex items-start gap-4 p-4">
        <StationPhoto
          photoUrl={showPhoto ? photoUrl : null}
          stationName={stationName}
        />
      </div>
      <div className="absolute right-3 top-3">
        <HeartButton stationId={stationId} />
      </div>
    </div>
  );
}

function StatsRow({
  maxPower,
  pricingInfo,
}: {
  maxPower: number;
  pricingInfo: StationDetailType["pricingInfo"];
}) {
  const { t } = useTranslation();
  const rate = renderRate(t, pricingInfo);
  return (
    <section className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-line bg-surface p-4">
        <p className="label-caps text-fg-mute">{t("stations.max_power_label")}</p>
        <p className="mt-1 flex items-baseline gap-1">
          <CountUpNumber value={maxPower} className="data text-volt" />
          <span className="num text-sm text-fg-mute">kW</span>
        </p>
      </div>
      <div className="rounded-lg border border-line bg-surface p-4">
        <p className="label-caps text-fg-mute">{t("stations.rate_label")}</p>
        <p className="mt-1 text-fg">{rate}</p>
      </div>
    </section>
  );
}

function OperatorSection({
  company,
  paymentNote,
}: {
  company: string | null;
  paymentNote: string | null;
}) {
  const { t } = useTranslation();
  if (!company && !paymentNote) return null;
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      {company && (
        <div className="flex items-baseline justify-between gap-3">
          <p className="label-caps text-fg-mute">{t("stations.operator_label")}</p>
          <p className="text-sm font-semibold text-fg">{company}</p>
        </div>
      )}
      {paymentNote && (
        <div className={company ? "mt-3 border-t border-line pt-3" : undefined}>
          <p className="label-caps text-fg-mute">{t("stations.payment_heading")}</p>
          <p className="mt-1 text-sm text-fg-mute">{paymentNote}</p>
        </div>
      )}
    </section>
  );
}

function renderRate(
  t: (key: string) => string,
  pricingInfo: StationDetailType["pricingInfo"],
) {
  if (!pricingInfo) return <span className="text-fg-faint">—</span>;
  if (pricingInfo.type === "free") {
    return <span className="text-volt">{t("stations.price_free")}</span>;
  }
  return (
    <span>
      <span className="num text-volt">
        {formatCop(pricingInfo.price_per_kwh ?? 0)}
      </span>
      <span className="num text-xs text-fg-mute">/kWh</span>
    </span>
  );
}

function CountUpNumber({ value, className = "" }: { value: number; className?: string }) {
  const reduceMotion = prefersReducedMotion();
  const mv = useMotionValue(reduceMotion ? value : 0);
  const display = useTransform(mv, (latest) => Math.round(latest).toString());
  useEffect(() => {
    if (reduceMotion) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [value, mv, reduceMotion]);
  return <motion.span className={className}>{display}</motion.span>;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const COP_FORMAT = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

function formatCop(value: number): string {
  return COP_FORMAT.format(value);
}

type PlugGroupAvail = {
  connector_type: ConnectorType;
  power_kw: number;
  count: number;
  available: number;
  offline: number;
  allUnknown: boolean;
};

function groupChargersWithAvailability(chargers: ReadonlyArray<ChargerInfo>): PlugGroupAvail[] {
  const byKey = new Map<string, PlugGroupAvail>();
  for (const c of chargers) {
    const key = `${c.connector_type}|${c.power_kw}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
      if (c.status === "available") existing.available += 1;
      if (c.status === "offline") existing.offline += 1;
      if (c.status !== "unknown") existing.allUnknown = false;
    } else {
      byKey.set(key, {
        connector_type: c.connector_type,
        power_kw: c.power_kw,
        count: 1,
        available: c.status === "available" ? 1 : 0,
        offline: c.status === "offline" ? 1 : 0,
        allUnknown: c.status === "unknown",
      });
    }
  }
  return sortPlugGroups([...byKey.values()]);
}

function chipToneFor(g: PlugGroupAvail): {
  name: AvailabilityTone;
  chip: string;
  dot: string;
  count: string;
} {
  if (g.allUnknown) {
    return {
      name: "unknown",
      chip: isFastConnector(g.connector_type)
        ? "border-volt/25 bg-volt/5 text-volt-deep"
        : "border-line bg-surface text-fg-mute",
      dot: "bg-current",
      count: "text-fg",
    };
  }
  if (g.available > 0) {
    return {
      name: "available",
      chip: "border-volt/30 bg-volt/5 text-volt-deep",
      dot: "bg-volt shadow-[0_0_8px_-2px_var(--color-volt-glow)]",
      count: "text-volt-deep",
    };
  }
  if (g.offline === g.count) {
    return {
      name: "offline",
      chip: "border-ember/30 bg-ember/5 text-ember",
      dot: "bg-transparent border border-ember",
      count: "text-ember",
    };
  }
  return {
    name: "busy",
    chip: "border-amber/30 bg-amber/5 text-amber",
    dot: "bg-amber",
    count: "text-amber",
  };
}

function PlugsSection({ chargers }: { chargers: ChargerInfo[] }) {
  const { t } = useTranslation();
  if (chargers.length === 0) return null;
  const groups = groupChargersWithAvailability(chargers);
  return (
    <section>
      <h2 className="label-caps mb-2 text-fg-mute">
        {t("stations.connectors_available", { count: chargers.length })}
      </h2>
      <ul className="flex flex-wrap gap-2">
        {groups.map((g) => {
          const tone = chipToneFor(g);
          const connectorLabel = CONNECTOR_LABEL[g.connector_type];
          return (
            <li
              key={`${g.connector_type}-${g.power_kw}`}
              data-testid={`plug-chip-${g.connector_type}-${g.power_kw}`}
              data-tone={tone.name}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${tone.chip}`}
            >
              <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
              <span>{connectorLabel}</span>
              <span className="num text-fg-faint">{formatKw(g.power_kw)} kW</span>
              {g.allUnknown && g.count > 1 && (
                <span className="num rounded-full bg-surface-raised px-1.5 py-0.5 text-[10px] text-fg">
                  ×{g.count}
                </span>
              )}
              {!g.allUnknown && (
                <span
                  className={`num rounded-full bg-surface-raised px-1.5 py-0.5 text-[10px] ${tone.count}`}
                  aria-label={t("stations.chip_availability_aria", {
                    free: g.available,
                    total: g.count,
                    connector: connectorLabel,
                  })}
                >
                  {t("stations.chip_availability", { free: g.available, total: g.count })}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function navigateUrl(station: { lat: number; lng: number; name: string }): string {
  const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad/.test(navigator.userAgent);
  if (isMobile) return `geo:${station.lat},${station.lng}?q=${encodeURIComponent(station.name)}`;
  return `https://www.google.com/maps?q=${station.lat},${station.lng}`;
}
